import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "@/constants/config";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import TRACKING_SERVICE_URL from "../../constants/tracking";

export default function VehicleStopsPreview() {
  const [vehicle, setVehicle] = useState(null);
  const [route, setRoute] = useState(null);
  const [selectedShiftId, setSelectedShiftId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const urlShiftId = urlParams.get("shiftId") || "";
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [mapReady, setMapReady] = useState(false);
  const mapTilerKey = import.meta.env.VITE_MAPTILER_API_KEY;
  const INITIAL_CENTER = [77.08, 28.48];
  const INITIAL_ZOOM = 12;

  useEffect(() => {
    try {
      const storedVehicle = JSON.parse(localStorage.getItem("vehicle") || "null");
      setVehicle(storedVehicle);
    } catch {
      setVehicle(null);
    }
  }, []);

  useEffect(() => {
    if (!vehicle) {
      setLoading(false);
      return;
    }

    const fetchRoute = async () => {
      try {
        setLoading(true);
        setError("");
        const vehicleId = vehicle?._id || vehicle?.id;
        if (!vehicleId) {
          throw new Error("Vehicle id missing in session");
        }

        const shiftsRes = await axiosInstance.get(
          `${API_ENDPOINTS.vehicleRoutes}/vehicle/${vehicleId}/shifts`
        );
        const shifts = shiftsRes?.data?.data?.shifts || [];
        const vehicleRouteId = shiftsRes?.data?.data?.vehicleRouteId || null;

        const myRoute = {
          _id: vehicleRouteId,
          shifts,
        };
        setRoute(myRoute);
        if (shifts.length) {
          const matchedShift = urlShiftId && shifts.find((s) => s._id === urlShiftId);
          setSelectedShiftId(matchedShift ? urlShiftId : shifts[0]._id);
        }
      } catch (e) {
        setError(e?.response?.data?.message || "Failed to fetch route stops.");
      } finally {
        setLoading(false);
      }
    };

    fetchRoute();
  }, [vehicle]);

  useEffect(() => {
    if (!mapTilerKey) {
      setError("Map key not configured.");
      return;
    }
    if (loading) return;
    if (mapRef.current) return;
    const container = document.getElementById("vehicle-stops-preview-map");
    if (!container) return;
    const map = new maplibregl.Map({
      container,
      style: `https://api.maptiler.com/maps/streets/style.json?key=${mapTilerKey}`,
      center: INITIAL_CENTER,
      zoom: INITIAL_ZOOM,
    });
    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.on("load", () => setMapReady(true));
    mapRef.current = map;

    return () => {
      markersRef.current.forEach((m) => {
        try {
          m.remove();
        } catch {
          // ignore
        }
      });
      markersRef.current = [];
      if (mapRef.current) {
        try {
          mapRef.current.remove();
        } catch {
          // ignore
        }
      }
      mapRef.current = null;
    };
  }, [mapTilerKey, loading]);

  const shifts = useMemo(() => route?.shifts || [], [route]);
  const selectedShift = useMemo(
    () => shifts.find((s) => s._id === selectedShiftId) || null,
    [shifts, selectedShiftId]
  );
  const stops = selectedShift?.stops || [];

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    const clearMap = () => {
      markersRef.current.forEach((m) => {
        try {
          m.remove();
        } catch {
          // ignore
        }
      });
      markersRef.current = [];

      if (mapRef.current.getSource("vehicle-stops-route-preview")) {
        try {
          if (mapRef.current.getLayer("vehicle-stops-route-preview-line")) {
            mapRef.current.removeLayer("vehicle-stops-route-preview-line");
          }
          mapRef.current.removeSource("vehicle-stops-route-preview");
        } catch {
          // ignore
        }
      }
    };

    const fitCoords = (coords) => {
      if (!coords.length) return;
      const bounds = new maplibregl.LngLatBounds();
      coords.forEach((coord) => bounds.extend(coord));
      mapRef.current.fitBounds(bounds, { padding: 70, maxZoom: 16, duration: 700 });
    };

    const addLine = (coords, dashed = false) => {
      if (!coords || coords.length < 2) return;
      mapRef.current.addSource("vehicle-stops-route-preview", {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: { type: "LineString", coordinates: coords },
        },
      });
      mapRef.current.addLayer({
        id: "vehicle-stops-route-preview-line",
        type: "line",
        source: "vehicle-stops-route-preview",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#4629eb",
          "line-width": 4,
          "line-opacity": 0.75,
          ...(dashed ? { "line-dasharray": [2, 2] } : {}),
        },
      });
      fitCoords(coords);
    };

    const drawPreview = async () => {
      clearMap();

      const validStops = (stops || [])
        .map((s) => ({
          ...s,
          lat: Number(s.latitude),
          lng: Number(s.longitude),
        }))
        .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng));

      if (validStops.length === 0) {
        mapRef.current.flyTo({
          center: INITIAL_CENTER,
          zoom: INITIAL_ZOOM,
          duration: 500,
        });
        return;
      }

      validStops.forEach((stop, index) => {
        const el = document.createElement("div");
        const bg = stop.isCheckpoint ? "#ffa500" : index === 0 ? "#00bcd4" : index === validStops.length - 1 ? "#ff6b35" : "#4629eb";
        el.innerHTML = `<div style="width:28px;height:28px;background:${bg};border:2px solid white;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:11px;box-shadow:0 2px 8px rgba(0,0,0,0.3)">${index + 1}</div>`;
        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([stop.lng, stop.lat])
          .setPopup(
            new maplibregl.Popup({ offset: 20 }).setHTML(
              `<strong>${stop.name}</strong>${stop.isCheckpoint ? "<br/>Checkpoint" : ""}`
            )
          )
          .addTo(mapRef.current);
        markersRef.current.push(marker);
      });

      if (validStops.length === 1) {
        mapRef.current.flyTo({
          center: [validStops[0].lng, validStops[0].lat],
          zoom: 15,
          duration: 700,
        });
        return;
      }

      const stopCoords = validStops.map((s) => [s.lng, s.lat]);
      const tracePolyline =
        Array.isArray(selectedShift?.routePolyline) && selectedShift.routePolyline.length >= 2
          ? selectedShift.routePolyline
              .map((p) => [Number(p.lng), Number(p.lat)])
              .filter(([lng, lat]) => Number.isFinite(lat) && Number.isFinite(lng))
          : [];

      if (tracePolyline.length >= 2) {
        addLine(tracePolyline, false);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${TRACKING_SERVICE_URL}/api/vehicle-routes/ors-directions`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            coordinates: stopCoords,
            profile: "driving-car",
          }),
        });
        const data = await res.json();
        const orsCoordinates = data?.features?.[0]?.geometry?.coordinates;
        if (Array.isArray(orsCoordinates) && orsCoordinates.length >= 2) {
          addLine(orsCoordinates, false);
          return;
        }
      } catch {
        // fallback below
      }

      addLine(stopCoords, true);
    };

    drawPreview();
  }, [mapReady, selectedShift, stops]);

  if (loading) {
    return <div className="max-w-3xl mx-auto mt-8 text-gray-600">Loading stops preview...</div>;
  }

  if (!vehicle) {
    return <div className="max-w-3xl mx-auto mt-8 text-gray-600">Vehicle session not found. Please login again.</div>;
  }

  if (error) {
    return <div className="max-w-3xl mx-auto mt-8 text-red-600">{error}</div>;
  }

  if (!route) {
    return <div className="max-w-3xl mx-auto mt-8 text-gray-600">No route assigned for this vehicle.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-4 bg-white">
      <h2 className="text-xl font-bold mb-4">Stops Preview</h2>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Shift</label>
        <select
          value={selectedShiftId}
          onChange={(e) => setSelectedShiftId(e.target.value)}
          className="w-full border rounded px-3 py-2"
        >
          {shifts.map((shift) => (
            <option key={shift._id} value={shift._id}>
              {shift.name} ({shift.startTime} - {shift.endTime})
            </option>
          ))}
        </select>
      </div>

      <div
        id="vehicle-stops-preview-map"
        className="w-full rounded border border-gray-200 bg-gray-50 mb-4"
        style={{ height: "420px" }}
      />

      {stops.length === 0 ? (
        <div className="text-gray-600">No stops found for selected shift.</div>
      ) : null}
    </div>
  );
}

